import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  TemplateRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DateFormats, Employee } from '@coin/shared/core/util-models';
import { V2ButtonComponent } from '../button/v2-button.component';
import { V2DividerComponent } from '../divider/v2-divider.component';
import { V2TextAreaComponent } from '../text-area/v2-text-area.component';
import { V2CommentComponent } from './comment/v2-comment.component';

export interface Comment {
  createdBy: Employee;
  value: string;
  createdAt: Date | string | number;
  id: string;
}

type CommentsByDate = { [date: string]: Comment[] };

@Component({
  selector: 'coin-v2-comments',
  imports: [
    CommonModule,
    V2CommentComponent,
    V2ButtonComponent,
    TranslateModule,
    ReactiveFormsModule,
    V2TextAreaComponent,
    V2DividerComponent
  ],
  templateUrl: './v2-comments.component.html',
  styleUrl: './v2-comments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DateFormats.UTC]
})
export class V2CommentsComponent {
  comments = input.required<Comment[]>();
  currentUserId = input.required<string>(); // required to differentiate own comments
  commentExtension = input<TemplateRef<unknown>>();
  @Output() sendComment = new EventEmitter<string>();

  private readonly translate = inject(TranslateService);

  protected textInput = new FormControl('');

  protected commentsGroupedByDate = computed(() => {
    const comments = this.comments() ?? [];
    return [...comments]
      .sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix()) // sort comments by newest first
      .reduce<CommentsByDate>((commentsByDate, comment) => {
        const isToday = moment().utc().isSame(comment.createdAt, 'day');
        const weekDay = isToday ? `[${this.translate.instant('general.today')}]` : 'dddd';
        const dateOfComment = moment(comment.createdAt).format(`${weekDay}, MM/DD/YYYY`);
        commentsByDate[dateOfComment] ??= [];
        commentsByDate[dateOfComment].push(comment);
        return commentsByDate;
      }, {});
  });

  // sort days by oldest first --> displays newest at the bottom, because of "flex-direction: column-reverse"
  protected sortByDateAscending = (a: KeyValue<string, Comment[]>, b: KeyValue<string, Comment[]>): number => {
    return moment(b.value[0].createdAt).unix() - moment(a.value[0].createdAt).unix();
  };

  protected send() {
    if (!this.textInput.value) return;
    this.sendComment.emit(this.textInput.value);
    this.textInput.setValue('');
  }
}
